/**
 * Financial API Service
 *
 * SWR hooks for fetching budget and expense data
 */

import useSWR from 'swr'
import { apiClient } from '../../shared/api/client'
import type { FinancialOverview } from '../../types/modules'

/**
 * Fetch financial overview for a given year
 *
 * GET /finances?year={year}
 */
export function useFinancialOverview(year: number = 2567) {
  const { data, error, isLoading, mutate } = useSWR<FinancialOverview>(
    [`/finances`, { year: year.toString() }],
    apiClient.fetcher,
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      refreshInterval: 5 * 60 * 1000, // 5 minutes
    }
  )

  return {
    data,
    error,
    isLoading,
    mutate,
  }
}

/**
 * Fetch financial data for specific district
 *
 * GET /finances/by-district/{districtId}?year={year}
 */
export function useFinancialByDistrict(districtId: number, year: number = 2567) {
  const { data, error, isLoading, mutate } = useSWR<FinancialOverview>(
    districtId ? [`/finances/by-district/${districtId}`, { year: year.toString() }] : null,
    apiClient.fetcher
  )

  return {
    data,
    error,
    isLoading,
    mutate,
  }
}

/**
 * Fetch budget category distribution across districts
 *
 * GET /finances/by-category/{categoryId}?year={year}
 */
export function useFinancialByCategory(categoryId: number, year: number = 2567) {
  const { data, error, isLoading, mutate } = useSWR(
    categoryId ? [`/finances/by-category/${categoryId}`, { year: year.toString() }] : null,
    apiClient.fetcher
  )

  return {
    data,
    error,
    isLoading,
    mutate,
  }
}

/**
 * Fetch multi-year financial trends
 *
 * GET /finances/trends?district_id={id}&category_type={income|expense}
 */
export function useFinancialTrends(filters?: {
  districtId?: number
  categoryType?: 'income' | 'expense'
}) {
  const params: Record<string, string> = {}

  if (filters?.districtId) {
    params.district_id = filters.districtId.toString()
  }
  if (filters?.categoryType) {
    params.category_type = filters.categoryType
  }

  const { data, error, isLoading, mutate } = useSWR<{
    filters: typeof params
    trends: Array<{
      year: number
      total_budget: string
      total_spent: string
      utilization_rate: string
    }>
  }>(
    [`/finances/trends`, params],
    apiClient.fetcher,
    {
      revalidateOnFocus: false,
      dedupingInterval: 60 * 1000, // Cache for 1 minute
    }
  )

  return {
    data,
    error,
    isLoading,
    mutate,
  }
}

/**
 * Fetch budget categories (reference data)
 *
 * GET /budget-categories
 */
export function useBudgetCategories() {
  const { data, error, isLoading } = useSWR(
    '/budget-categories',
    apiClient.fetcher,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      refreshInterval: 60 * 60 * 1000, // Cache for 1 hour
    }
  )

  return {
    data,
    error,
    isLoading,
  }
}
